import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BoardHeaderActions from './components/BoardHeaderActions';
import { useBoardFeature } from './sharedBoard';
import { sentFilters, shownFilters } from './filterDefaults';
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import { useWideBoard } from '../DashboardRoute';
import { fetchFilterOptions, fetchManualDashboard } from '../../services/manualDashboardService';
import type {
  Block, BreakdownTableBlockDef, FilterDef, ManualDashboardPayload, MetricDef, RetentionBlockDef,
  SpecTableBlockDef,
} from '../../types/manualDashboard';
import DashboardToolbar from './components/DashboardToolbar';
import KpiRow from './components/KpiRow';
import SpecTablePanel from './components/SpecTablePanel';
import BreakdownTablePanel from './components/BreakdownTablePanel';
import RetentionPanel from './components/RetentionPanel';
import BoardChatPanel from './components/BoardChatPanel';
import { boardChat } from './boardChat';
import { cohortOptions, formatValue, maturityNote, toSparklinePoints } from './format';
import { useSectionGrain } from './useSectionGrain';
import { buildBoardSurfaceContext } from './buildBoardSurfaceContext';
import { createSurfaceChatStore } from '../surface-chat/createSurfaceChatStore';
import { useSurfaceChat } from '../surface-chat/useSurfaceChat';
import dayjs from 'dayjs';
import {
  BarList,
  DATE_PRESETS,
  FunnelChart,
  formatDateRange,
  ISO_DAY,
  isWholeMonths,
  PageHeader,
  Panel,
  SegmentedControl,
  SelectMenu,
  TrendChart,
  resolvePreset,
  type DateRange,
  type DateRangePreset,
} from '../../ui-kit';

/** Which panels a pending request actually invalidates. */
type LoadScope = 'all' | 'segments';

/**
 * The board opens on the last 12 months up to today, as the other boards run
 * to today — on 22 Sep 2026, 1 Oct 2025 to 22 Sep 2026. The month in progress
 * is its own cohort, labelled as cut short ("Sep 26 (1–22)").
 */
const DEFAULT_PRESET = DATE_PRESETS.last12MonthsToDate;

const BOARD_PRESETS: DateRangePreset[] = [
  DEFAULT_PRESET,
  DATE_PRESETS.thisMonth,
  DATE_PRESETS.lastMonth,
  DATE_PRESETS.thisQuarter,
  DATE_PRESETS.lastQuarter,
  DATE_PRESETS.yearToDate,
  DATE_PRESETS.lastYear,
];

const WEEK_STARTS_ON = 1;

const CHAT_WIDTH_DEFAULT = 420;
const CHAT_WIDTH_MIN = 320;
const CHAT_WIDTH_MAX = 860;
const CHAT_WIDTH_KEY = 'klaris_board_chat_width';

// One store per mounted board. Created outside the component so the
// conversation survives re-renders, and keyed by dashboard so two boards do
// not share a thread.
const chatStores = new Map<string, ReturnType<typeof createSurfaceChatStore>>();
function chatStoreFor(dashboardId: string, name: string) {
  let store = chatStores.get(dashboardId);
  if (!store) {
    store = createSurfaceChatStore(name);
    chatStores.set(dashboardId, store);
  }
  return store;
}

export default function ManualDashboardPage() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const feature = useBoardFeature();

  const [payload, setPayload] = React.useState<ManualDashboardPayload | null>(null);
  const [filters, setFilters] = React.useState<FilterDef[]>([]);
  const [applied, setApplied] = React.useState<Record<string, string[]>>({});
  // A filter with a default (the Scorecard's Lead type, on MQL) shows it until
  // the reader changes it; `sent` is what every request for the board carries.
  const shown = React.useMemo(() => shownFilters(applied, filters), [applied, filters]);
  // Keyed by content: the counts reload with the picks below, and fresh
  // counts must not read as a change of filters and reload the board.
  const sentKey = JSON.stringify(sentFilters(applied, filters));
  const sent = React.useMemo<Record<string, string[]>>(() => JSON.parse(sentKey), [sentKey]);
  // Only a board with an include filter counts each choice against the others.
  const countsFollowPicks = filters.some((f) => f.kind === 'include');
  const [range, setRange] = React.useState<DateRange>(() =>
    resolvePreset(DEFAULT_PRESET, dayjs(), WEEK_STARTS_ON),
  );
  const [dimension, setDimension] = React.useState<string | undefined>(undefined);
  const [trendMetric, setTrendMetric] = React.useState<string | null>(null);
  const [segmentMetric, setSegmentMetric] = React.useState<string | null>(null);
  const [measure, setMeasure] = React.useState<'count' | 'premium'>('count');
  // Cohort grouping a chart or table was switched to. Unset follows the
  // board's own grouping, which the server picks from the range.
  const [sectionGrain, setSectionGrain] = React.useState<{ trend?: string }>({});

  const [loading, setLoading] = React.useState(true);
  const [scope, setScope] = React.useState<LoadScope>('all');
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  // Bumping this re-runs the fetch with refresh=true, which rebuilds the
  // cached rows from the warehouse. Every other interaction reads the cache.
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [chatOpen, setChatOpen] = React.useState(false);

  // Panel width is dragged and remembered, the same way the Lead-to-Cash
  // chat is: a board read beside an answer often wants more room than the
  // default, and re-dragging it on every visit is a papercut.
  const [chatWidth, setChatWidth] = React.useState<number>(() => {
    try {
      const stored = Number(window.localStorage.getItem(CHAT_WIDTH_KEY));
      if (Number.isFinite(stored) && stored >= CHAT_WIDTH_MIN && stored <= CHAT_WIDTH_MAX) {
        return stored;
      }
    } catch {
      // ignore
    }
    return CHAT_WIDTH_DEFAULT;
  });
  const dragStateRef = React.useRef<{ startX: number; startWidth: number } | null>(null);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidth));
    } catch {
      // ignore
    }
  }, [chatWidth]);

  const onResizeStart = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startWidth: chatWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [chatWidth]);

  const onResizeMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const ds = dragStateRef.current;
    if (!ds) return;
    // Dragging left widens the panel, so the delta is subtracted.
    const dx = e.clientX - ds.startX;
    setChatWidth(Math.min(CHAT_WIDTH_MAX, Math.max(CHAT_WIDTH_MIN, ds.startWidth - dx)));
  }, []);

  const onResizeEnd = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);
  // Available before the payload loads, so the chat is never titled 'Board'.
  const boardName =
    (useLocation().state as { dashboardName?: string } | null)?.dashboardName ?? 'Dashboard';
  const [error, setError] = React.useState<string | null>(null);

  // Drop the layout's page gutter; take over scrolling once chat is docked, so
  // the board and the chat share one full-height viewport.
  useWideBoard(true, chatOpen);

  // A new range changes which groupings make sense, so every section goes
  // back to following the board's.
  React.useEffect(() => {
    setSectionGrain({});
  }, [range]);

  // A section's choice only counts while the range still offers it.
  const availableGrains = payload?.available_grains ?? [];
  const offered = (grain?: string) =>
    grain && availableGrains.some((g) => g.key === grain) ? grain : undefined;
  const trendGrain = offered(sectionGrain.trend) ?? payload?.grain;

  // Tables regroup themselves (SpecTablePanel) and report their grouping
  // here, so the export and chat know what each is showing.
  const [tableGrains, setTableGrains] = React.useState<Record<string, string | undefined>>({});
  const onTableGrainChange = React.useCallback((id: string, grain: string | undefined) => {
    setTableGrains((prev) => (prev[id] === grain ? prev : { ...prev, [id]: grain }));
  }, []);

  const trendOverride = useSectionGrain({
    dashboardId, grain: trendGrain, boardGrain: payload?.grain, range, filters: sent, refreshNonce,
  });


  // Changing the break-by dimension only re-cuts the segment panel; the range
  // and filters change every number on the page. Tracking which one moved
  // lets the shimmer sit on the panel that is actually going to change,
  // instead of blanking a board whose other figures are still correct.
  // (Cohort grouping follows the range; the server picks it.)
  const previous = React.useRef({ sent, range });
  React.useEffect(() => {
    const same = previous.current.sent === sent && previous.current.range === range;
    setScope(same ? 'segments' : 'all');
    previous.current = { sent, range };
  }, [sent, range, dimension]);

  React.useEffect(() => {
    if (!dashboardId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchManualDashboard(
      dashboardId,
      {
        dimension, filters: sent, start: range.start, end: range.end,
        refresh: refreshNonce > 0,
      },
      controller.signal,
    )
      .then((data) => {
        setPayload(data);
        setDimension((current) => current ?? data.selected_dimension ?? undefined);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, dimension, sent, range, refreshNonce]);

  // Options depend on the window, so they refetch on the range; on a board
  // with an include filter, on the picks too.
  const optionPicks = countsFollowPicks ? sentKey : '';
  React.useEffect(() => {
    if (!dashboardId) return undefined;
    const controller = new AbortController();
    setOptionsLoading(true);
    fetchFilterOptions(
      dashboardId,
      { start: range.start, end: range.end, filters: optionPicks ? JSON.parse(optionPicks) : undefined },
      controller.signal,
    )
      .then((data) => setFilters(data.filters))
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setOptionsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, range, refreshNonce, optionPicks]);

  // Chat, docked right. The surface context is read through a ref so the
  // send callback never changes identity as the board reloads — otherwise
  // every filter change would tear down the chat's handlers mid-stream.
  const chatStore = chatStoreFor(dashboardId ?? 'unknown', boardName);
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);
  const getSurfaceContext = React.useCallback(() => surfaceContextRef.current, []);
  // The conversation is found from the database, not remembered in this
  // browser: the same board opens the same chat on any machine.
  const { sendMessage, sendSQLApproval, stopStream } = useSurfaceChat({
    useStore: chatStore,
    dashboardId: dashboardId ?? '',
    getSurfaceContext,
    enabled: chatOpen,
  });

  const metricsByKey = React.useMemo(() => {
    const map = new Map<string, MetricDef>();
    payload?.metrics.forEach((m) => map.set(m.key, m));
    return map;
  }, [payload]);

  const blocks = payload?.blocks ?? [];
  const find = <T extends Block['type']>(type: T) =>
    blocks.find((b) => b.type === type) as Extract<Block, { type: T }> | undefined;

  const retentions = blocks.filter((b): b is RetentionBlockDef => b.type === 'retention');
  const kpi = find('kpi_row');
  const funnel = find('funnel');
  const trend = find('trend');
  const segments = find('segments');

  const activeTrendMetric = trendMetric ?? trend?.default_metric ?? null;
  const activeSegmentMetric = segmentMetric ?? segments?.default_metric ?? null;
  const trendMetricDef = activeTrendMetric ? metricsByKey.get(activeTrendMetric) : undefined;
  const segmentMetricDef = activeSegmentMetric ? metricsByKey.get(activeSegmentMetric) : undefined;

  // Metric dropdowns redraw from data already in the browser, so only a
  // scope:'all' fetch dims the shared panels.
  const wholeBoardLoading = loading && scope === 'all';
  const segmentsLoading = loading;

  if (feature.loading) {
    return <Box sx={{ px: 0.5 }}><Skeleton variant="rounded" height={320} /></Box>;
  }
  if (!feature.enabled) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="info">
          Manual dashboards are not enabled for this workspace. Ask an admin to turn on the
          &ldquo;Manual Dashboards&rdquo; feature.
        </Alert>
      </Box>
    );
  }
  if (error && !payload) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="error" onClose={() => navigate('/dashboard')}>{error}</Alert>
      </Box>
    );
  }
  if (!payload) {
    return (
      <Stack spacing={1.5} sx={{ px: 0.5 }}>
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={104} />
        <Skeleton variant="rounded" height={300} />
      </Stack>
    );
  }

  // A section regrouped away from the board's default reads its block from
  // its own payload; otherwise from the board's.
  const blockIn = <T extends Block['type']>(source: ManualDashboardPayload | null, type: T) =>
    source?.blocks.find((b) => b.type === type) as Extract<Block, { type: T }> | undefined;
  const trendView = blockIn(trendOverride.payload, 'trend') ?? trend;

  // Tables sit where their spec puts them — under the headline figures, or
  // after the charts — in the order the spec lists them.
  type TableBlock = SpecTableBlockDef | BreakdownTableBlockDef;
  const tables = blocks.filter(
    (b): b is TableBlock => b.type === 'spec_table' || b.type === 'breakdown_table',
  );
  const topTables = tables.filter((b) => b.placement === 'top');
  const bottomTables = tables.filter((b) => b.placement !== 'top');

  const renderTable = (table: TableBlock, where: 'top' | 'bottom') => {
    const shared = {
      key: table.id,
      dashboardId,
      payload,
      range,
      filters: sent,
      refreshNonce,
      loading: wholeBoardLoading,
      collapsible: true,
      onGrainChange: onTableGrainChange,
    };
    return table.type === 'spec_table' ? (
      <SpecTablePanel
        {...shared}
        block={table}
        loadingHeight={where === 'top' ? 280 : 360}
        // Below the charts, a table opens with every section folded to its
        // title: the long detail, laid out as a contents list to open from.
        groupsCollapsed={where === 'bottom'}
      />
    ) : (
      <BreakdownTablePanel {...shared} block={table} />
    );
  };

  const trendPoints = trendMetricDef ? trendView?.series[trendMetricDef.key] : undefined;

  // What one row of the board is — a case, a family — in the board's words.
  const noun = payload.spec.noun ?? { one: 'case', many: 'cases' };
  const Many = noun.many.charAt(0).toUpperCase() + noun.many.slice(1);
  // This board's own chat words, over the cohort board's.
  const chatPlace = payload.spec.chat
    ? {
        ...boardChat,
        welcome: payload.spec.chat.welcome ? { ...boardChat.welcome, subtitle: payload.spec.chat.welcome } : boardChat.welcome,
        starters: payload.spec.chat.starters ?? boardChat.starters,
        placeholder: payload.spec.chat.placeholder ?? boardChat.placeholder,
      }
    : boardChat;

  // Groupings this range offers, each with how many cohorts it gives.
  const grainOptions = cohortOptions(payload);
  const trendCohortSelect =
    grainOptions.length > 1 ? (
      <SelectMenu
        label="Cohort"
        value={trendGrain ?? payload.grain}
        options={grainOptions}
        onChange={(next) => setSectionGrain({ trend: next })}
      />
    ) : null;
  const sectionError = trendOverride.error;

  // Both metric selects offer the whole catalogue, grouped as the spec groups
  // it, each with its definition so the choice can be made knowingly.
  const metricOptions = payload.metrics.map((m) => ({
    value: m.key, label: m.label, group: m.group, description: m.definition,
  }));
  const activeDimension = dimension ?? segments?.dimension ?? null;
  const dimensionLabel = payload.dimensions.find((d) => d.key === activeDimension)?.label ?? 'Segment';

  surfaceContextRef.current = buildBoardSurfaceContext({
    dashboardId: dashboardId ?? '',
    payload,
    trendMetric: activeTrendMetric,
    segmentMetric: activeSegmentMetric,
    dimension: dimension ?? segments?.dimension ?? null,
    filterDefs: filters,
    sectionGrains: Object.fromEntries(
      Object.entries({ trend: trendGrain, ...tableGrains }).filter(
        (entry): entry is [string, string] => Boolean(entry[1]) && entry[1] !== payload.grain,
      ),
    ),
  });

  // What the chat's answers are scoped to, shown in its composer: the range
  // and each applied filter, the same scope sent as surface context above.
  // The board starts where its history does, so a range reaching back
  // further is shown as the span the numbers actually cover.
  const shownRange = range.start < payload.history_start ? { ...range, start: payload.history_start } : range;
  const chatScope = [
    { key: 'range', label: formatDateRange(shownRange, isWholeMonths(shownRange) ? 'month' : 'day') },
    ...Object.entries(payload.selected_filters)
      .filter(([, values]) => values.length > 0)
      .map(([key, values]) => {
        const label = filters.find((f) => f.key === key)?.label ?? key;
        return { key, label: `${label}: ${values[0]}${values.length > 1 ? ` +${values.length - 1}` : ''}` };
      }),
  ];

  // Each chart is drawn twice when expanded — once in its panel, once full
  // screen — so it is built here, once.
  const funnelChart = funnel ? (
    <FunnelChart
      steps={funnel.stages.map((s) => ({
        key: s.key, label: s.label, value: s[measure], lossLabel: s.drop_label,
      }))}
      formatValue={(v) => formatValue(v, measure === 'count' ? 'int' : 'money')}
    />
  ) : null;
  const trendChart = (height?: number | string) =>
    trendPoints && trendMetricDef ? (
      <TrendChart
        points={toSparklinePoints(trendPoints)}
        seriesLabel={trendMetricDef.label}
        formatValue={(v) => formatValue(v, trendMetricDef.format)}
        height={height}
      />
    ) : null;
  const segmentBars =
    segments && segmentMetricDef ? (
      <BarList
        rows={segments.bands.map((b) => ({
          key: b.band,
          label: b.band,
          value: b.values[segmentMetricDef.key] ?? null,
          secondary: b.count,
        }))}
        formatValue={(v) => formatValue(v, segmentMetricDef.format)}
        valueLabel={segmentMetricDef.label}
        labelHeading={dimensionLabel}
        secondaryLabel={segments.show_denominator ? Many : undefined}
        formatSecondary={(v) => formatValue(v, 'int')}
        emptyMessage={`No ${noun.many} match this selection.`}
      />
    ) : null;

  const board = (
    <Box sx={{ px: 0.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0 }}>
      {/* Named like every dashboard, with how current it is and Chat at the
          top right. */}
      <PageHeader
        ariaLabel="About this dashboard"
        title={payload.spec.name}
        description={payload.spec.description ?? undefined}
        actions={
          <BoardHeaderActions
            dashboardId={dashboardId ?? ''}
            name={payload.spec.name}
            updatedAt={payload.meta.built_at}
            status={payload.meta.refreshing
                  ? 'updating'
                  : payload.meta.status === 'failed' ? 'failed' : payload.meta.stale ? 'stale' : 'fresh'}
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); setRefreshNonce((n) => n + 1); }}
            loading={loading}
            chatOpen={chatOpen}
            onToggleChat={() => setChatOpen((v) => !v)}
            chatTooltip="Ask about this board"
          />
        }
      />

      <DashboardToolbar
        dateRange={shownRange}
        onDateRangeChange={setRange}
        datePresets={BOARD_PRESETS}
        minDate={payload.history_start}
        maxDate={dayjs().format(ISO_DAY)}
        filters={filters}
        applied={shown}
        onApply={setApplied}
        loading={loading}
        optionsLoading={optionsLoading}
        chatOpen={chatOpen}
      />

      {payload.meta.status === 'failed' && payload.meta.error && (
        <Alert severity="warning">
          Could not refresh from the warehouse, so these are the numbers from{' '}
          {payload.meta.built_at ? new Date(payload.meta.built_at).toLocaleString() : 'the last successful build'}.
          <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
            {payload.meta.error}
          </Typography>
        </Alert>
      )}


      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}
      {sectionError && (
        <Alert severity="warning">Could not regroup a section: {sectionError}</Alert>
      )}

      {kpi && <KpiRow metrics={kpi.metrics} loading={wholeBoardLoading} />}

      {topTables.map((table) => renderTable(table, 'top'))}

      {/* Funnel beside the cohort trend. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.5, alignItems: 'stretch' }}>
        {funnel && (
          <Panel
            title={funnel.title}
            subtitle={`How ${noun.many} carry from one stage to the next, across the whole range.`}
            tools={
              funnel.measure_toggle.length > 1 ? (
                <SegmentedControl
                  ariaLabel="Measure"
                  value={measure}
                  onChange={setMeasure}
                  options={funnel.measure_toggle.map((m) => ({
                    value: m as 'count' | 'premium',
                    label: m === 'count' ? Many : 'Premium',
                  }))}
                />
              ) : null
            }
            loading={wholeBoardLoading}
            loadingHeight={240}
            expanded={funnelChart ? { content: funnelChart } : undefined}
          >
            {funnelChart}
          </Panel>
        )}

        {trend && (
          <Panel
            title={trend.title}
            subtitle={trendMetricDef ? `${trendMetricDef.label}, by cohort` : undefined}
            info={trendMetricDef?.definition ?? undefined}
            tools={
              <>
                {trendCohortSelect}
                <SelectMenu
                  label="Metric"
                  value={activeTrendMetric ?? ''}
                  options={metricOptions}
                  onChange={setTrendMetric}
                />
              </>
            }
            loading={wholeBoardLoading || trendOverride.loading}
            loadingHeight={240}
            footer={trendPoints && trendMetricDef ? maturityNote(trendPoints, trendMetricDef) : null}
            expanded={trendPoints && trendMetricDef ? { content: trendChart('100%') } : undefined}
          >
            {trendPoints && trendMetricDef ? (
              trendChart()
            ) : (
              <Skeleton variant="rounded" height={240} />
            )}
          </Panel>
        )}
      </Box>

      {/* Retention grids across the full width: one column per week since signup. */}
      {retentions.map((block) => (
        <RetentionPanel
          key={block.id}
          dashboardId={dashboardId}
          block={block}
          payload={payload}
          range={range}
          filters={sent}
          refreshNonce={refreshNonce}
          loading={wholeBoardLoading}
          onGrainChange={onTableGrainChange}
        />
      ))}

      {/* Segments across the full width: long band lists need it. */}
      {segments && (
        <Panel
          title={segments.title}
          subtitle={segmentMetricDef ? `${segmentMetricDef.label}, by ${dimensionLabel.toLowerCase()}` : undefined}
          info={segmentMetricDef?.definition ?? undefined}
          tools={
            <>
              <SelectMenu
                label="Break by"
                value={activeDimension ?? ''}
                options={payload.dimensions.map((d) => ({ value: d.key, label: d.label }))}
                onChange={setDimension}
              />
              <SelectMenu
                label="Metric"
                value={activeSegmentMetric ?? ''}
                options={metricOptions}
                onChange={setSegmentMetric}
              />
            </>
          }
          loading={segmentsLoading}
          loadingHeight={180}
          expanded={segmentBars ? { content: segmentBars } : undefined}
          footer={
            segments.show_denominator
              ? `Read each rate against how many ${noun.many} it rests on before acting on it: a high rate on a handful of ${noun.many} means little.`
              : null
          }
        >
          {segmentMetricDef ? (
            segmentBars
          ) : (
            <Skeleton variant="rounded" height={180} />
          )}
        </Panel>
      )}

      {bottomTables.map((table) => renderTable(table, 'bottom'))}
    </Box>
  );

  if (!chatOpen) return board;

  // Chat is a column beside the board, not a drawer over it — the point is to
  // read the numbers and the answer at the same time.
  return (
    // No gap on this row: the resize handle alone separates board and chat,
    // so the two sit as close as the rest of the page's panels.
    <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, minHeight: 0, height: '100%' }}>
      {/* The board scrolls inside its own column so the chat beside it stays
          anchored to the full height of the viewport. */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin' }}>
        {board}
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        title="Drag to resize · double-click to reset"
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        onDoubleClick={() => setChatWidth(CHAT_WIDTH_DEFAULT)}
        sx={(t) => ({
          flexShrink: 0,
          width: t.spacing(1.5),
          cursor: 'col-resize',
          position: 'relative',
          // A hairline down the middle that shows where to grab.
          '&::after': {
            content: '""',
            position: 'absolute',
            top: t.spacing(1),
            bottom: t.spacing(1),
            left: '50%',
            width: '1px',
            bgcolor: 'transparent',
            transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          },
          '&:hover::after, &:active::after': { bgcolor: t.tokens.brand.primary },
        })}
      />

      <BoardChatPanel
        useStore={chatStore}
        width={chatWidth}
        boardName={payload.spec.name}
        onSend={sendMessage}
        onStop={stopStream}
        onSQLApproval={sendSQLApproval}
        place={chatPlace}
        contextChips={chatScope}
        onClose={() => setChatOpen(false)}
      />
    </Box>
  );
}
